import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import { FaUserPlus, FaPhone, FaInfoCircle, FaLock, FaCheckCircle } from "react-icons/fa";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, Link } from "react-router-dom";
import { firestore } from "../Firebase";
import Bg from "../Images/regis.jpg";

const Registration = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    studentNumber: "",
    program: "",
    yearOfStudy: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackSeverity, setFeedbackSeverity] = useState("success");
  const [registered, setRegistered] = useState(false);
  const navigate = useNavigate();

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      color: "white",
      "& fieldset": {
        borderColor: "rgba(0, 217, 255, 0.3)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0, 217, 255, 0.5)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#00d9ff",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.7)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#00d9ff",
    },
    "& .MuiSelect-icon": {
      color: "rgba(255, 255, 255, 0.7)",
    },
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setFeedbackMessage("Passwords do not match");
      setFeedbackSeverity("error");
      setFeedbackOpen(true);
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setFeedbackMessage("Password must be at least 6 characters");
      setFeedbackSeverity("error");
      setFeedbackOpen(true);
      return;
    }

    setLoading(true);

    try {
      // Check if student number already exists
      const existingSnapshot = await firestore
        .collection("registrations")
        .where("studentNumber", "==", formData.studentNumber)
        .get();

      if (!existingSnapshot.empty) {
        setFeedbackMessage("This student number is already registered");
        setFeedbackSeverity("error");
        setFeedbackOpen(true);
        setLoading(false);
        return;
      }

      // Save to Firestore with pending status
      await firestore.collection("registrations").add({
        fullName: formData.fullName,
        studentNumber: formData.studentNumber,
        program: formData.program,
        yearOfStudy: formData.yearOfStudy,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        status: "pending",
        createdAt: new Date(),
      });

      setRegistered(true);
      setFeedbackMessage("Registration successful! Please pay K30 to activate your account.");
      setFeedbackSeverity("success");
      setFeedbackOpen(true);
    } catch (error) {
      console.error("Registration error:", error);
      setFeedbackMessage("An error occurred. Please try again.");
      setFeedbackSeverity("error");
      setFeedbackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(90deg, #ffffff, #00d9ff)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Join Us
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.7)",
              mb: 4,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Become a member of ICTAZ NIPA Chapter and start your journey in tech innovation
          </Typography>
        </motion.div>

        {/* Notice Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Card
            sx={{
              borderRadius: "20px",
              background: "linear-gradient(135deg, rgba(0, 217, 255, 0.15), rgba(0, 180, 216, 0.1))",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(0, 217, 255, 0.4)",
              boxShadow: "0 8px 32px rgba(0, 217, 255, 0.2)",
              mb: 4,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaInfoCircle size={24} color="white" />
                </Box>
                <Typography variant="h6" fontWeight={700} color="white">
                  How to Become a Member
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255, 255, 255, 0.9)", lineHeight: 1.8 }}
              >
                <strong>Step 1:</strong> Fill out the registration form below<br />
                <strong>Step 2:</strong> Pay <strong style={{ color: "#00d9ff" }}>K30</strong> affiliation fee to the Treasurer General<br />
                <strong>Step 3:</strong> Your account will be approved and you'll gain access to member resources
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: "12px",
                  background: "rgba(0, 0, 36, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <FaPhone size={20} color="#00d9ff" />
                <Typography variant="body1" sx={{ color: "white" }}>
                  Contact/WhatsApp:{" "}
                  <strong style={{ color: "#00d9ff" }}>+260972451530</strong>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </motion.div>

        {/* Registration Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card
            sx={{
              borderRadius: "25px",
              ...darkGlassStyle,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "15px",
                      background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaUserPlus size={28} color="white" />
                  </Box>
                </motion.div>
                <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                  Registration Form
                </Typography>
              </Box>

              {registered ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, type: "spring" }}
                  >
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #22c55e, #16a34a)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 3,
                      }}
                    >
                      <FaCheckCircle size={50} color="white" />
                    </Box>
                  </motion.div>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: 'white' }}>
                    Registration Successful!
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.8)", mb: 3 }}>
                    Your account has been created. Please pay <strong style={{ color: "#00d9ff" }}>K30</strong> to the Treasurer General to activate your membership.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate("/Client/login")}
                      sx={{
                        borderRadius: "12px",
                        px: 4,
                        background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                        fontWeight: 600,
                      }}
                    >
                      Login Now
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setRegistered(false);
                        setFormData({
                          fullName: "",
                          studentNumber: "",
                          program: "",
                          yearOfStudy: "",
                          phoneNumber: "",
                          password: "",
                          confirmPassword: "",
                        });
                      }}
                      sx={{
                        borderRadius: "12px",
                        px: 4,
                        borderColor: "rgba(0, 217, 255, 0.5)",
                        color: "#00d9ff",
                      }}
                    >
                      Register Another
                    </Button>
                  </Box>
                </Box>
              ) : (
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="fullName"
                      variant="outlined"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      sx={inputStyle}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Student Number"
                      name="studentNumber"
                      variant="outlined"
                      value={formData.studentNumber}
                      onChange={handleChange}
                      required
                      sx={inputStyle}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phoneNumber"
                      variant="outlined"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      required
                      sx={inputStyle}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={inputStyle}>
                      <InputLabel>Program of Study</InputLabel>
                      <Select
                        name="program"
                        value={formData.program}
                        onChange={handleChange}
                        label="Program of Study"
                        required
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: "#0a192f",
                              "& .MuiMenuItem-root": {
                                color: "white",
                                "&:hover": {
                                  bgcolor: "rgba(0, 217, 255, 0.2)",
                                },
                                "&.Mui-selected": {
                                  bgcolor: "rgba(0, 217, 255, 0.3)",
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="Dip in Computer Science">Dip in Computer Science</MenuItem>
                        <MenuItem value="Bsc Computer Science">Bsc Computer Science</MenuItem>
                        <MenuItem value="Dip Information Systems">Dip Information Systems</MenuItem>
                        <MenuItem value="Bsc Information Systems">Bsc Information Systems</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={inputStyle}>
                      <InputLabel>Year of Study</InputLabel>
                      <Select
                        name="yearOfStudy"
                        value={formData.yearOfStudy}
                        onChange={handleChange}
                        label="Year of Study"
                        required
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: "#0a192f",
                              "& .MuiMenuItem-root": {
                                color: "white",
                                "&:hover": {
                                  bgcolor: "rgba(0, 217, 255, 0.2)",
                                },
                                "&.Mui-selected": {
                                  bgcolor: "rgba(0, 217, 255, 0.3)",
                                },
                              },
                            },
                          },
                        }}
                      >
                        {["1:1", "1:2", "2:1", "2:2", "3:1", "3:2", "4:1", "4:2"].map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      sx={inputStyle}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaLock color="#00d9ff" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{ color: "rgba(255,255,255,0.7)" }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      name="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      sx={inputStyle}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaLock color="#00d9ff" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        sx={{
                          mt: 2,
                          py: 1.5,
                          borderRadius: "12px",
                          background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          textTransform: "none",
                          boxShadow: "0 4px 20px rgba(0, 217, 255, 0.4)",
                          "&:hover": {
                            background: "linear-gradient(135deg, #00b4d8, #0096c7)",
                            boxShadow: "0 6px 25px rgba(0, 217, 255, 0.5)",
                          },
                          "&:disabled": {
                            background: "rgba(0, 217, 255, 0.3)",
                          },
                        }}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FaUserPlus />}
                      >
                        {loading ? "Registering..." : "Register Now"}
                      </Button>
                    </motion.div>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 2,
                        textAlign: "center",
                        color: "rgba(255, 255, 255, 0.6)",
                      }}
                    >
                      Already have an account?{" "}
                      <Link
                        to="/Client/login"
                        style={{
                          color: "#00d9ff",
                          textDecoration: "none",
                          fontWeight: 600,
                        }}
                      >
                        Login here
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Feedback Snackbar */}
        <Snackbar
          open={feedbackOpen}
          autoHideDuration={5000}
          onClose={() => setFeedbackOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setFeedbackOpen(false)}
            severity={feedbackSeverity}
            sx={{ width: "100%", borderRadius: "12px" }}
          >
            {feedbackMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Registration;
